import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
} from '@mui/material';
import { useForm } from 'react-hook-form';

import { selectUserTrial } from '@/domain/auth/auth.selectors';
import { ExportType, ProjectType } from '@/domain/pdf-reports/pdf-reports.type';
import { exportProjectReport } from '@/domain/project/actions/export-report-project.action';
import { selectIsExportingReport } from '@/domain/project/project.selectors';
import { ProjectFestival } from '@/domain/project-festival/entities/project-festival.entity';
import { ProjectTour } from '@/domain/project-tour/entities/project-tour.entity';
import { ProjectVenue } from '@/domain/project-venue/entities/project-venue.entity';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSSecondaryButton } from '@/ui/components/button/FSSecondaryButton';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { FSModal } from '@/ui/components/modal/FSModal';
import { FSModalTitle } from '@/ui/components/modal/FSModalTitle';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { createExportProjectFilename } from '@/ui/utils/create-export-project-filename';

export function ModalReportExporter({
  open,
  onClose,
  project,
  projectType,
}: {
  open: boolean;
  onClose: () => void;
  project: ProjectFestival | ProjectVenue | ProjectTour;
  projectType: ProjectType;
}) {
  const dispatch = useAppDispatch();

  const isExportingReport = useAppSelector(selectIsExportingReport);

  const onSubmit = async (data: ReportExporterFormData) => {
    try {
      await dispatch(
        exportProjectReport({
          projectId: project.id,
          filename: data.fileName,
          exportType: data.exportType,
          projectType: projectType,
        }),
      ).unwrap();
      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <FSModal open={open} onClose={onClose}>
      <Stack spacing={3}>
        <FSModalTitle title={translate('ReportExporter.title')} />
        <ReportExporterForm
          onSubmit={onSubmit}
          onCancel={onClose}
          projectName={project.name}
          projectType={projectType}
          isExportingReport={isExportingReport}
        />
      </Stack>
    </FSModal>
  );
}

type ReportExporterFormData = {
  projectId: number;
  fileName: string;
  exportType: ExportType;
};

function defaultPdfName(projectName: string): string {
  return createExportProjectFilename(projectName, 'pdf');
}

function replaceExtension(filename: string, exportType: ExportType) {
  switch (exportType) {
    case ExportType.ONLY_RESULTS:
    case ExportType.FULL_ANSWER_AND_RESULTS:
      return `${filename.replace(/\.(pdf|zip|xlsx)$/, '.pdf')}`;
    case ExportType.SOCIAL_MEDIA_SCORE_CARDS:
      return `${filename.replace(/\.(pdf|zip|xlsx)$/, '.zip')}`;
    case ExportType.EXCEL_CO2_ANSWERS:
      return `${filename.replace(/\.(pdf|zip|xlsx)$/, '.xlsx')}`;
  }
}

function validateFileName(value: string, exportType: ExportType) {
  switch (exportType) {
    case ExportType.ONLY_RESULTS:
    case ExportType.FULL_ANSWER_AND_RESULTS:
      if (!value.endsWith('.pdf')) {
        return translate('ReportExporter.Form.file-name-must-be-a-pdf');
      }
      break;
    case ExportType.SOCIAL_MEDIA_SCORE_CARDS:
      if (!value.endsWith('.zip')) {
        return translate('ReportExporter.Form.file-name-must-be-a-zip');
      }
      break;
    case ExportType.EXCEL_CO2_ANSWERS:
      if (!value.endsWith('.xlsx')) {
        return translate('ReportExporter.Form.file-name-must-be-a-xlsx');
      }
      break;
  }
  const invalidChars = /[<>:"/\\|?*]/g;
  if (invalidChars.test(value)) {
    return translate('ReportExporter.Form.invalid-characters');
  }
  return true;
}

function ReportExporterForm({
  onCancel,
  onSubmit,
  projectName,
  projectType,
  isExportingReport,
}: {
  onCancel: () => void;
  onSubmit: (data: ReportExporterFormData) => void;
  projectName: string;
  projectType: ProjectType;
  isExportingReport: boolean;
}) {
  const isOnTrial = useAppSelector(selectUserTrial).isOnTrial;

  const enableExcelExport = projectType === ProjectType.FESTIVAL;
  const enableScoreCardsExport = projectType !== ProjectType.TOUR;

  const defaultValues: Partial<ReportExporterFormData> = {
    fileName: defaultPdfName(projectName),
    exportType: ExportType.ONLY_RESULTS,
  };
  const { handleSubmit, register, watch, setValue, formState } =
    useForm<ReportExporterFormData>({
      defaultValues,
    });
  const exportType = watch('exportType');
  const { errors } = formState;

  const handleExportTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newExportType = event.target.value as ExportType;
    setValue('exportType', newExportType);

    // Get current filename and update it with new extension
    const currentFileName = watch('fileName');
    const newFileName = replaceExtension(currentFileName, newExportType);
    setValue('fileName', newFileName);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={2}>
        <FSTextField
          label={translate('ReportExporter.Form.new-filename')}
          maxLength={255}
          register={{
            ...register('fileName', {
              required: translate('Common.field-is-required'),
              validate: (value) => validateFileName(value, exportType),
            }),
          }}
          error={!!errors.fileName}
          helperText={errors.fileName?.message}
        />
        <RadioGroup value={exportType} onChange={handleExportTypeChange}>
          <FormControlLabel
            value={ExportType.ONLY_RESULTS}
            control={<Radio />}
            label={translate('ReportExporter.Form.only-results-checkbox')}
          />
          <FormControlLabel
            value={ExportType.FULL_ANSWER_AND_RESULTS}
            control={<Radio />}
            disabled={true}
            label={translate('ReportExporter.Form.with-answers-checkbox')}
          />
          {enableScoreCardsExport && (
            <FormControlLabel
              value={ExportType.SOCIAL_MEDIA_SCORE_CARDS}
              control={<Radio />}
              label={translate('ReportExporter.Form.social-media-score-cards')}
            />
          )}
          {enableExcelExport && (
            <FormControlLabel
              value={ExportType.EXCEL_CO2_ANSWERS}
              control={<Radio />}
              label={translate('ReportExporter.Form.excel-co2-answers')}
            />
          )}
        </RadioGroup>
      </Stack>
      <Tooltip
        title={translate('ReportExporter.Form.trial-limitation')}
        arrow
        open={isOnTrial}
      >
        <Stack direction={'row'} justifyContent={'flex-end'} sx={{ mt: 2 }}>
          <FSSecondaryButton onClick={onCancel} disabled={isExportingReport}>
            {translate('ReportExporter.Form.cancel')}
          </FSSecondaryButton>
          <FSPrimaryButton
            type="submit"
            disabled={isOnTrial || isExportingReport}
          >
            {isExportingReport
              ? translate('ReportExporter.Form.please-wait')
              : translate('ReportExporter.Form.export')}
          </FSPrimaryButton>
        </Stack>
      </Tooltip>
    </form>
  );
}
